import { Tooltip, Chip } from '@mui/material'
import { CloudQueue, AccountTree, Folder, HelpOutline } from '@mui/icons-material'

/**
 * IdentitySourceBadge — surfaces how the server-side repo identity was resolved
 * for a given analysis. Phase 6 of SCAN_IDENTITY_PLAN.
 *
 * Sources and meanings:
 * - git_remote        : scanner read .git/ and got the canonical remote URL. Best case.
 * - ci_env            : scanner couldn't read .git/ but the CI env var set provided a URL.
 *                       Common in shallow-clone CI configs. Equally trustworthy.
 * - workspace_basename: legacy fallback — basename(workspace_path). Unstable across users.
 *                       Surfaces an "upgrade scanner" hint.
 * - legacy_*          : pre-Phase-4 analysis rows; same kind of warning.
 */
type Source =
  | 'git_remote'
  | 'ci_env'
  | 'workspace_basename'
  | 'legacy_workspace_path_hash'
  | 'legacy_repository_name'
  | null
  | undefined

interface IdentitySourceBadgeProps {
  source: Source
}

const IdentitySourceBadge = ({ source }: IdentitySourceBadgeProps) => {
  if (!source) return null

  if (source === 'git_remote') {
    return (
      <Tooltip title="Identity from git remote URL — stable across users and CI">
        <Chip
          icon={<AccountTree sx={{ fontSize: 14 }} />}
          label="git"
          size="small"
          variant="outlined"
          color="success"
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
        />
      </Tooltip>
    )
  }

  if (source === 'ci_env') {
    return (
      <Tooltip title="Identity from CI environment variables — stable across users">
        <Chip
          icon={<CloudQueue sx={{ fontSize: 14 }} />}
          label="ci"
          size="small"
          variant="outlined"
          color="success"
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
        />
      </Tooltip>
    )
  }

  if (source === 'workspace_basename') {
    return (
      <Tooltip title="Identity from workspace path — unstable across users. Re-run install.sh to upgrade scanner.">
        <Chip
          icon={<Folder sx={{ fontSize: 14 }} />}
          label="legacy"
          size="small"
          variant="outlined"
          color="warning"
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
        />
      </Tooltip>
    )
  }

  // legacy_* sources — analyses persisted before Phase 4
  if (source === 'legacy_workspace_path_hash' || source === 'legacy_repository_name') {
    return (
      <Tooltip title="Pre-2026-05-18 analysis — predates stable git identity. New scans will use the modern identity.">
        <Chip
          icon={<HelpOutline sx={{ fontSize: 14 }} />}
          label="pre-2026-05"
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
        />
      </Tooltip>
    )
  }

  return null
}

export default IdentitySourceBadge
